/**
 * API Configuration
 * Central configuration for all API endpoints
 */

export const API_CONFIG = {
  // Base URL for CMS API - can be overridden by environment variables
  BASE_URL: process.env.API_URL || "http://localhost:3011/api",

  // Base URL for images - can be overridden by environment variables
  IMG_URL: process.env.IMG_URL || "http://localhost:3011",

  // Default request settings
  DEFAULT_HEADERS: {
    "Content-Type": "application/json",
  },

  // Request timeout in milliseconds
  TIMEOUT: 30000,

  // Default pagination settings
  PAGINATION: {
    DEFAULT_LIMIT: 10,
    MAX_LIMIT: 100,
  },

  // Retry configuration
  RETRY: {
    MAX_ATTEMPTS: 3,
    DELAY: 1000, // milliseconds
    BACKOFF_MULTIPLIER: 2,
  },

  // Cache settings for Next.js
  CACHE: {
    DEFAULT_REVALIDATE: 60, // seconds
    NO_CACHE: 0,
  },
} as const;

/**
 * API Endpoints configuration
 */
export const API_ENDPOINTS = {
  // Collections
  COLLECTIONS: {
    LANDING_CONFIG: "landing-config",
    MEDIA: "media",
    POSTS: "posts",
    PAGES: "pages",
    NEWS: "landing-news",
    LIBRARY: "landing-library",
  },

  // Globals
  GLOBALS: {
    SITE_SETTINGS: "site-settings",
    NAVIGATION: "navigation",
    FOOTER: "footer",
    LANDING_POLICY: "landing-policy",
    LANDING_CONTACT: "landing-contact",
  },
} as const;

/**
 * Get full API URL
 */
export function getApiUrl(endpoint: string): string {
  const baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, ""); // Remove trailing slash
  const cleanEndpoint = endpoint.replace(/^\//, ""); // Remove leading slash
  return `${baseUrl}/${cleanEndpoint}`;
}

/**
 * Get full image URL
 */
export function getImageUrl(imagePath: string): string {
  if (!imagePath) return "";

  // If already absolute URL, return as is
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  const baseUrl = API_CONFIG.IMG_URL.replace(/\/$/, ""); // Remove trailing slash
  const cleanPath = imagePath.replace(/^\//, ""); // Remove leading slash
  return `${baseUrl}/${cleanPath}`;
}

/**
 * Build query string from params object
 */
export function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  const appendParam = (key: string, value: any) => {
    if (value === undefined || value === null || value === "") return;

    if (typeof value === "object" && !Array.isArray(value)) {
      Object.entries(value).forEach(([subKey, subValue]) => {
        appendParam(`${key}[${subKey}]`, subValue);
      });
    } else {
      searchParams.append(key, String(value));
    }
  };

  Object.entries(params).forEach(([key, value]) => {
    appendParam(key, value);
  });

  return searchParams.toString();
}
